import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";

import { t } from "../i18n/i18n";
import { Motion, usePresence } from "./presence";
import { useMediaQuery } from "./useMediaQuery";
import { Waiting } from "./Waiting";

/** Voile de rédaction (demande d'Alexis du 11/10/2026) : tant que l'IA rédige un document affiché en
 *  feuilles A4 (fiche pays, mise en ambiance, OPO), les feuilles sont floutées sous un léger voile et ne se
 *  modifient pas ; une carte centrée dans la partie visible (elle suit le défilement) fait patienter, avec
 *  l'avancement réel quand il est connu. À la fin, à l'arrêt ou à l'échec, le flou se dissipe et la carte
 *  s'efface en douceur (`VEIL_EXIT_MS`). Mouvement réduit : flou simple, sans animation, retiré d'un coup.
 *
 *  Le voile ne couvre que les feuilles : l'en-tête de la vue, le bandeau de la rédaction (« Arrêter ») et la
 *  navigation restent utilisables. Aucun filtre ne reste sur les feuilles une fois le voile parti (ombre des
 *  pages rognée par Safari sous un filtre, voir `.opo-reveal`). */

/** Durée de la dissipation du flou, celle de `--gx-veil` (index.css). */
export const VEIL_EXIT_MS = 800;

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

/** État du voile : posé (`on`), en train de se dissiper (`leaving`), absent (`off`). */
export type VeilPhase = "on" | "leaving" | "off";

export function veilPhase(state: { mounted: boolean; closing: boolean }): VeilPhase {
  return !state.mounted ? "off" : state.closing ? "leaving" : "on";
}

/** Durée de la sortie : aucune en mouvement réduit. */
export function veilExitMs(reduced: boolean): number {
  return reduced ? 0 : VEIL_EXIT_MS;
}

/** Avancement de la rédaction : sections terminées sur le total attendu, section en cours (texte traduit). */
export interface GenerationProgress {
  done: number;
  total: number;
  current: string | null;
}

/** Avancement d'une rédaction diffusée (documentJob.ts) : sections terminées parmi celles que l'IA doit
 *  écrire (`expected`, ou celles demandées), étape en cours ; une étape à venir n'est pas faite. Les étapes de
 *  lecture du dossier et des gabarits ne comptent pas. Total jamais inférieur aux sections déjà commencées. */
export function jobProgress(
  job: { targets: string[] | null; steps: { id: string; label: string; state: string }[] },
  expected: readonly string[],
  label: (raw: string) => string,
): GenerationProgress {
  const steps = job.steps.filter((s) => s.id !== "dossier" && s.id !== "templates");
  // Étapes à venir (`pending`, annoncées d'emblée par le serveur) ni en cours : pas encore faites.
  const done = steps.filter((s) => s.state !== "running" && s.state !== "pending").length;
  const wanted = job.targets ? job.targets.filter((id) => expected.includes(id)) : expected;
  const total = Math.max(wanted.length, steps.length);
  const running = job.steps.find((s) => s.state === "running");
  return { done: Math.min(done, total), total, current: running ? label(running.label) : null };
}

/** Feuilles du document (`children`) sous le voile tant que `active`. `count` : « Rubriques rédigées : 3
 *  sur 12 », selon le document ; `note` : ligne sous le libellé quand l'avancement n'est pas connu ;
 *  `delay` : le voile ne vient qu'après ce délai (ms), pour qu'une demande refusée aussitôt ne fasse pas
 *  clignoter les feuilles. */
export function GenerationVeil({
  active,
  progress,
  count,
  note,
  delay = 0,
  children,
}: {
  active: boolean;
  progress?: GenerationProgress | null;
  count?: (done: number, total: number) => string;
  note?: string;
  delay?: number;
  children: ReactNode;
}) {
  const reduced = useMediaQuery(REDUCED_MOTION);
  const shown = useEntryDelay(active, delay);
  const phase = veilPhase(usePresence(shown, veilExitMs(reduced)));
  return (
    <VeilFrame phase={phase} progress={progress} count={count} note={note}>
      {children}
    </VeilFrame>
  );
}

/** Rendu du voile pour un état donné (testé sans navigateur, GenerationVeil.test.tsx). Le conteneur des
 *  feuilles ne change jamais de place dans l'arbre : rien n'est remonté quand le voile vient ou part. */
export function VeilFrame({
  phase,
  progress,
  count,
  note,
  children,
}: {
  phase: VeilPhase;
  progress?: GenerationProgress | null;
  count?: (done: number, total: number) => string;
  note?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useVisibleHeight(ref, phase !== "off");
  return (
    <div ref={ref} className="relative" aria-busy={phase === "on" || undefined}>
      <div
        className={phase === "off" ? undefined : "gx-veil-content"}
        data-veil={phase === "off" ? undefined : phase}
        // Feuilles non modifiables pendant la rédaction : ni clic, ni focus, ni double-clic d'édition.
        inert={phase === "on" || undefined}
      >
        {children}
      </div>
      {phase !== "off" && (
        <div className="gx-veil-layer absolute inset-0 z-10 bg-canvas/25" data-veil={phase}>
          {/* Carte collée à la partie visible : centrée dans la hauteur de la zone qui défile, tant que les
              feuilles la couvrent. */}
          <div
            className="sticky top-0 flex items-center justify-center px-4"
            style={{ height: visible ? `min(100%, ${visible}px)` : "min(100%, 70vh)" }}
          >
            <VeilCard progress={progress} count={count} note={note} />
          </div>
        </div>
      )}
    </div>
  );
}

function VeilCard({
  progress,
  count,
  note,
}: {
  progress?: GenerationProgress | null;
  count?: (done: number, total: number) => string;
  note?: string;
}) {
  const known = Boolean(progress && progress.total > 0);
  const share = known && progress ? Math.round((progress.done / progress.total) * 100) : 0;
  return (
    <div
      role="status"
      className={
        "flex w-full max-w-[22rem] items-start gap-4 rounded-xl border border-line bg-surface/95 px-5 py-4 " +
        "shadow-lg"
      }
    >
      <WritingSheet />
      <div className="min-w-0 flex-1">
        <p className="text-ui leading-snug font-medium text-ink">
          <Waiting>{t("Rédaction en cours…")}</Waiting>
        </p>
        {known && progress && count ? (
          <p className="mt-1 text-meta leading-snug text-ink-soft tabular-nums">
            {count(progress.done, progress.total)}
          </p>
        ) : (
          note && <p className="mt-1 text-meta leading-snug text-ink-soft">{note}</p>
        )}
        {progress?.current && (
          <p className="mt-0.5 truncate text-meta leading-snug text-ink-muted" title={progress.current}>
            {t("En cours : {name}", { name: progress.current })}
          </p>
        )}
        <div
          className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-line"
          role="progressbar"
          aria-label={t("Avancement de la rédaction")}
          aria-valuemin={known ? 0 : undefined}
          aria-valuemax={known ? 100 : undefined}
          aria-valuenow={known ? share : undefined}
        >
          {known ? (
            <div
              className="gx-veil-bar h-full rounded-full bg-run"
              style={{ width: `${Math.max(share, 4)}%` }}
            />
          ) : (
            <div className="gx-veil-indeterminate h-full w-1/3 rounded-full bg-run" />
          )}
        </div>
      </div>
    </div>
  );
}

/** Petite feuille dont les lignes s'écrivent l'une après l'autre, comme le texte de l'IA sur les pages. */
function WritingSheet() {
  return (
    <div
      aria-hidden
      className={
        "relative mt-0.5 flex h-16 w-12 shrink-0 flex-col gap-1.5 rounded-sm border border-line-strong " +
        "bg-canvas px-2 py-2.5"
      }
    >
      <span className="gx-veil-line h-1 w-full rounded-full bg-run/70" />
      <span className="gx-veil-line h-1 w-full rounded-full bg-run/50" />
      <span className="gx-veil-line h-1 w-4/5 rounded-full bg-run/50" />
      <span className="gx-veil-line h-1 w-full rounded-full bg-run/50" />
      <span className="gx-veil-line h-1 w-3/5 rounded-full bg-run/50" />
    </div>
  );
}

/** `active` retardé de `delay` ms à l'entrée ; la sortie suit aussitôt. */
function useEntryDelay(active: boolean, delay: number): boolean {
  const [late, setLate] = useState(false);
  useEffect(() => {
    if (!active || delay <= 0) {
      setLate(false);
      return;
    }
    const timer = window.setTimeout(() => setLate(true), delay);
    return () => window.clearTimeout(timer);
  }, [active, delay]);
  return active && (delay <= 0 || late);
}

/** Hauteur visible de la zone qui défile autour des feuilles (la plus proche `.overflow-y-auto`), suivie au
 *  redimensionnement ; 0 tant qu'elle n'est pas connue. */
function useVisibleHeight(ref: { current: HTMLElement | null }, enabled: boolean): number {
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const scroller = enabled ? ref.current?.closest<HTMLElement>(".overflow-y-auto") : null;
    if (!scroller) return;
    const measure = () => setHeight(scroller.clientHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [enabled]);
  return height;
}

/** Rédaction lancée avant qu'aucune feuille n'existe (première composition de la fiche pays, premier OPO) :
 *  la même carte, seule, au milieu de la vue, en fondu. */
export function GenerationWait({
  progress,
  count,
  note,
}: {
  progress?: GenerationProgress | null;
  count?: (done: number, total: number) => string;
  note?: string;
}) {
  return (
    <Motion origin="fade" className="flex h-full items-center justify-center px-4 py-10">
      <VeilCard progress={progress} count={count} note={note} />
    </Motion>
  );
}
